import { useCallback, useMemo } from "react";
import Form, { List, Field as BaseField } from "rc-field-form";
import { firstToUpper } from "../../../../../utils/helpers/string";
import { useTranslation } from "react-i18next";
import { Input, UncontrolledTooltip } from "reactstrap";
import { faMinus, faPlus, faCircleExclamation, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomTimeInput from "../../../../../components/Inputs/CustomTimeInput";
import DateInput from "../../../../../components/Inputs/DateInput";
import Field from "../../../../../components/Field";
import moment from "moment-timezone";
import Switch from "../../../../../components/Inputs/Switch";
import ApprovedSnapshot from "../../ApprovedSnapshot";
import History from "../../History";
import { serializeDay } from "../../helpers";
import { applyTimezone, combineDateAndTime } from "../../../../../utils/helpers/date";
import { useCompanyTimeZone } from "../../../../../utils/hooks/company";
import "./style.scss";

const WeekdaysCard = ({ day, data, approved = [], history = [], initialValue, form, disabled }) => {
    const { t } = useTranslation();
    const formData = Form.useWatch(day, form);
    const startDate = Form.useWatch([day, "startDate"], form);
    const timeZone = useCompanyTimeZone();

    const today = useMemo(() => moment().startOf("day").toDate(), []);

    // Flipping either switch starts the day's times over from a single empty row.
    const onAllDayChange = useCallback((value) => {
        const allDay = value.target.checked;
        if (allDay === formData?.allDay) {
            return;
        }
        form.setFieldValue(day, {
            ...formData,
            allDay,
            isAvailable: typeof formData?.isAvailable === "undefined" ? true : formData?.isAvailable,
            intervals: [{ from: undefined, to: undefined }],
        });
    }, [day, form, formData]);

    const onAvailableChange = useCallback((value) => {
        const isAvailable = value.target.checked;
        if (isAvailable === formData?.isAvailable) {
            return;
        }
        form.setFieldValue(day, {
            ...formData,
            allDay: typeof formData?.allDay === "undefined" ? false : formData?.allDay,
            isAvailable,
            intervals: [{ from: undefined, to: undefined }],
        });
    }, [day, form, formData]);

    // Dates are only checked on edited days, so untouched rows never block the form.
    const isChanged = useCallback(
        (getFieldValue) => serializeDay(getFieldValue(day)) !== serializeDay(initialValue),
        [day, initialValue]
    );

    const toTimeValue = useCallback((value) => {
        if (timeZone) {
            value = applyTimezone(value, timeZone);
        }
        return value.toISOString(true);
    }, [timeZone]);

    const validateFrom = useCallback((getFieldValue, name, value) => {
        const date = moment().format("YYYY-MM-DD");
        let previousfrom = getFieldValue([day, "intervals", name - 1, "from"]);
        let previousto = getFieldValue([day, "intervals", name - 1, "to"]);
        let to = getFieldValue([day, "intervals", name, "to"]);

        previousfrom = previousfrom ? combineDateAndTime(moment(date), moment.parseZone(previousfrom)) : undefined;
        previousto = previousto ? combineDateAndTime(moment(date), moment.parseZone(previousto)) : undefined;
        to = to ? combineDateAndTime(moment(date), moment.parseZone(to)) : undefined;

        if (value) {
            value = combineDateAndTime(moment(date), moment.parseZone(value));

            if (to && moment(value).isSameOrAfter(moment(to), "seconds")) {
                return Promise.reject(new Error(t("start-smaller-than-end")));
            } else if (previousto && previousfrom && moment(value).isBetween(moment(previousfrom), moment(previousto), "seconds")) {
                return Promise.reject(new Error(t("start-between-previous-start-and-end-time")));
            } else if (previousfrom && moment(value).isSame(moment(previousfrom), "seconds")) {
                return Promise.reject(new Error(t("start-same-as-previous-start-time")));
            } else if (!to) {
                return Promise.reject(new Error(t("required-end")));
            }
        }
        return Promise.resolve();
    }, [day, t]);

    const validateTo = useCallback((getFieldValue, name, value) => {
        const date = moment().format("YYYY-MM-DD");
        let previousfrom = getFieldValue([day, "intervals", name - 1, "from"]);
        let previousto = getFieldValue([day, "intervals", name - 1, "to"]);
        let from = getFieldValue([day, "intervals", name, "from"]);

        previousfrom = previousfrom ? combineDateAndTime(moment(date), moment.parseZone(previousfrom)) : undefined;
        previousto = previousto ? combineDateAndTime(moment(date), moment.parseZone(previousto)) : undefined;
        from = from ? combineDateAndTime(moment(date), moment.parseZone(from)) : undefined;

        if (value) {
            value = combineDateAndTime(moment(date), moment.parseZone(value));
            if (from && moment(value).isSameOrBefore(moment(from), "seconds")) {
                return Promise.reject(new Error(t("end-must-be-same-or-bigger-than-start-time")));
            } else if (previousfrom && previousto && moment(value).isBetween(moment(previousfrom), moment(previousto), "seconds")) {
                return Promise.reject(new Error(t("end-time-cannot-be-between-previous-start-and-end-time")));
            } else if (
                from && previousfrom && previousto &&
                moment(from).isSameOrBefore(moment(previousfrom), "seconds") &&
                moment(value).isSameOrAfter(moment(previousto), "seconds")
            ) {
                return Promise.reject(new Error(t("shifts-cannot-overlap-each-other")));
            } else if (!from) {
                return Promise.reject(new Error(t("required-start")));
            }
        }
        return Promise.resolve();
    }, [day, t]);

    const hasPending = data?.status === "pending";
    const isApproved = !hasPending && approved.length > 0;
    const allDay = formData?.allDay;
    const isAvailable = formData?.isAvailable;

    return (
        <div className="d-flex flex-column gap-3 mb-2 border rounded p-4 recurring-weekday-card">
            <div className="d-flex align-items-center">
                <h3 className="weekdays-width m-0 font-weight-bolder text-dark">{firstToUpper(t(day))}</h3>

                <div className="d-flex flex-grow-1 align-items-center flex-wrap gap-4">
                    <Field name={[day, "allDay"]} className="mb-0" valuePropName="checked">
                        <Switch id={`allDay-${day}`} onChange={onAllDayChange} disabled={disabled}>
                            <span className="text-muted text-nowrap">{t("all-day")}</span>
                        </Switch>
                    </Field>

                    <div className="d-flex align-items-center gap-2">
                        <span className={isAvailable ? "text-muted" : "text-dark font-weight-bold"}>
                            {t("unavailable")}
                        </span>
                        <BaseField name={[day, "isAvailable"]} valuePropName="checked">
                            <Switch id={`isAvailable-${day}`} disabled={disabled} onChange={onAvailableChange}>
                                <span className={isAvailable ? "text-dark font-weight-bold" : "text-muted"}>
                                    {firstToUpper(t("available"))}
                                </span>
                            </Switch>
                        </BaseField>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2 ml-2">
                    <History id={`history-${day}`} title={firstToUpper(t(day))} history={history} />

                    {hasPending ? (
                        <>
                            <UncontrolledTooltip target={`pending-${day}`}>
                                {t("needs-approval-from-manager")}
                            </UncontrolledTooltip>
                            <span id={`pending-${day}`}>
                                <FontAwesomeIcon icon={faCircleExclamation} className="text-lg pending-button-style" />
                            </span>
                        </>
                    ) : null}

                    {isApproved ? (
                        <>
                            <UncontrolledTooltip target={`approve-${day}`}>
                                {t("approved")}
                            </UncontrolledTooltip>
                            <span id={`approve-${day}`}>
                                <FontAwesomeIcon icon={faCircleCheck} className="text-lg text-green" />
                            </span>
                        </>
                    ) : null}
                </div>
            </div>

            {approved.length > 0 && (
                <div className="weekdays-offset d-flex flex-column gap-2">
                    {approved.map((snapshot, i) => (
                        <ApprovedSnapshot key={i} id={`approved-${day}-${i}`} snapshot={snapshot} />
                    ))}
                </div>
            )}

            <div className="weekdays-offset d-flex align-items-start gap-3">
                <div className="d-flex flex-column gap-1 availability-date-input">
                    <small className="text-muted">
                        {firstToUpper(t("start-date"))} <span className="text-red">*</span>
                    </small>
                    <Field
                        name={[day, "startDate"]}
                        className="mb-0"
                        rules={[
                            ({ getFieldValue }) => ({
                                validator: (_, value) => {
                                    if (value && isChanged(getFieldValue) && moment(value).isBefore(moment(), "day")) {
                                        return Promise.reject(new Error(t("date-cannot-be-in-the-past")));
                                    }
                                    return Promise.resolve();
                                },
                            }),
                        ]}
                    >
                        <DateInput disabled={disabled} placeholderText={t("start-date")} minDate={today} />
                    </Field>
                </div>
                <div className="d-flex flex-column gap-1 availability-date-input">
                    <small className="text-muted">{firstToUpper(t("end-date"))}</small>
                    <Field
                        name={[day, "endDate"]}
                        className="mb-0"
                        dependencies={[[day, "startDate"]]}
                        rules={[
                            ({ getFieldValue }) => ({
                                validator: (_, value) => {
                                    if (!value || !isChanged(getFieldValue)) {
                                        return Promise.resolve();
                                    }
                                    const start = getFieldValue([day, "startDate"]);
                                    if (moment(value).isBefore(moment(), "day")) {
                                        return Promise.reject(new Error(t("date-cannot-be-in-the-past")));
                                    } else if (start && moment(value).isBefore(moment(start), "day")) {
                                        return Promise.reject(new Error(t("end-date-cannot-be-before-start-date")));
                                    }
                                    return Promise.resolve();
                                },
                            }),
                        ]}
                    >
                        <DateInput
                            disabled={disabled}
                            placeholderText={t("end-date")}
                            minDate={startDate || today}
                        />
                    </Field>
                </div>
            </div>

            {!(allDay && isAvailable) && (
                <div className="weekdays-offset d-flex flex-column gap-3">
                    {!isAvailable && (
                        <div className="d-flex flex-column gap-1">
                            <small className="text-muted">{t("reason")}</small>
                            <Field name={[day, "reason"]} className="mb-0 availability-reason-input">
                                <Input disabled={disabled} type="text" placeholder={t("reason")} />
                            </Field>
                        </div>
                    )}

                    {!allDay && (
                        <List name={[day, "intervals"]}>
                            {(fields, { add, remove }) => (
                                <div className="d-flex flex-column gap-2">
                                    {fields.length > 0 && (
                                        <div className="d-flex gap-3">
                                            <small className="text-muted availability-time-input">{t("from")}</small>
                                            <small className="text-muted availability-time-input">{firstToUpper(t("to"))}</small>
                                        </div>
                                    )}

                                    {fields.map(({ key, name }) => (
                                        <BaseField key={key} shouldUpdate>
                                            {(_, __, { getFieldsError }) => {
                                                const [error] = getFieldsError([
                                                    [day, "intervals", name, "from"],
                                                    [day, "intervals", name, "to"],
                                                ]).map(({ errors }) => errors).flat();

                                                return (
                                                    <div className="d-flex flex-column">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="availability-time-input">
                                                                <BaseField
                                                                    name={[name, "from"]}
                                                                    dependencies={[
                                                                        [day, "intervals", name, "to"],
                                                                        [day, "intervals", name - 1, "to"],
                                                                        [day, "intervals", name - 1, "from"],
                                                                    ]}
                                                                    rules={[({ getFieldValue }) => ({ validator: (_, value) => validateFrom(getFieldValue, name, value) })]}
                                                                >
                                                                    {({ value, onChange }) => (
                                                                        <CustomTimeInput
                                                                            disabled={disabled}
                                                                            value={value}
                                                                            onChange={(value) => onChange(toTimeValue(value))}
                                                                            placeholder={t("start")}
                                                                        />
                                                                    )}
                                                                </BaseField>
                                                            </div>
                                                            <div className="availability-time-input">
                                                                <BaseField
                                                                    name={[name, "to"]}
                                                                    dependencies={[
                                                                        [day, "intervals", name, "from"],
                                                                        [day, "intervals", name - 1, "from"],
                                                                        [day, "intervals", name - 1, "to"],
                                                                    ]}
                                                                    rules={[({ getFieldValue }) => ({ validator: (_, value) => validateTo(getFieldValue, name, value) })]}
                                                                >
                                                                    {({ value, onChange }) => (
                                                                        <CustomTimeInput
                                                                            disabled={disabled}
                                                                            value={value}
                                                                            onChange={(value) => onChange(toTimeValue(value))}
                                                                            placeholder={t("end")}
                                                                        />
                                                                    )}
                                                                </BaseField>
                                                            </div>
                                                            {(fields.length > 1 && !disabled) && (
                                                                <FontAwesomeIcon
                                                                    className="text-danger cursor-pointer"
                                                                    onClick={() => remove(name)}
                                                                    icon={faMinus}
                                                                />
                                                            )}
                                                        </div>
                                                        {error && <small className="text-danger mt-1">{error}</small>}
                                                    </div>
                                                );
                                            }}
                                        </BaseField>
                                    ))}

                                    {!disabled && (
                                        <div>
                                            <span className="text-primary cursor-pointer text-sm" onClick={() => add()}>
                                                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                                                {t("add")}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </List>
                    )}
                </div>
            )}
        </div>
    );
};

export default WeekdaysCard;
